import { useLatest } from '@solinkcorp/solink-components';
import throttle from 'lodash/throttle';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebouncedFunction } from 'src/hooks/useDebounce';
import useTimelineDrag, { type TimelineDragState } from './common/useTimelineDrag';
import { type TimelineEvent } from './types';

const DEFAULT_MAIN_CHART_RANGE = 15 * 60 * 1000; // 15 mins
export const MIN_MAIN_CHART_RANGE_MS = 60 * 1000; // 30 seconds
export const MAX_MAIN_CHART_RANGE_MS = 30 * 60 * 1000; // 30 minutes

const TRUNCATED_EVENT_LENGTH_LIMIT_MS = 5 * 60 * 1000; // 5 Minutes.

// If we switch hovered events too quickly, ignore the switch.
const HOVER_EVENT_DEBOUNCE_MS = 200;

const SET_RANGE_THROTTLE_MS = 1000 / 30;

export interface TimelineState {
  /**
   * If the user is hovering over an event in the timeline, this will be the event's id.
   */
  hoveredEvent: TimelineEvent | undefined;
  /**
   * The time to show as the current play time on the timeline.
   *
   * This is *usually* the same as `masterTime`, but not when the user is
   * dragging the timeline around.
   */
  playHeadTime: number;
  /** The time zone to play in. */
  timezone: string;
  /**
   * The range of the master chart in ms (this is either-side of masterTime,
   * so if this is 15 mins, mainChartStart will be 15 mins before masterTime,
   * and mainChartEnd will be 15 mins after).
   */
  mainChartRange: number;
  /**
   * The time (ms since epoch) of the left-hand edge of the main chart.
   * This is always `masterTime - mainChartRange`.
   */
  mainChartStart: number;
  /**
   * The time (ms since epoch) of the right-hand edge of the main chart.
   * This is always `masterTime + mainChartRange`.
   */
  mainChartEnd: number;
  /** The time (ms since epoch) of the left-hand edge of the MiniMap. */
  miniMapStart: number;
  /** The time (ms since epoch) of the right-hand edge of the MiniMap. */
  miniMapEnd: number;
  /**
   * The start of the day of mainChartStart - the time to start fetching
   * cached data from.
   */
  fetchStart: number;
  /** The end of the day of mainChartEnd. */
  fetchEnd: number;
  /**
   * True if we should show matchEvents and unmatchEvents, false if we should
   * only show matchEvents.
   */
  showAllEvents: boolean;
  /** If specified, events on the timeline should be truncated to this length. */
  eventLengthLimitMs: number | undefined;
  dragState: TimelineDragState;
}

export interface TimelineActions {
  /** Called when the user hovers over an event in the timeline. */
  setHoveredEvent(event: TimelineEvent | undefined): void;
  /**
   * A function to set the main chart range.  This will clamp the value to a
   * valid range, so there's no need to range check values you pass into this.
   */
  setMainChartRange(ms: number): void;
  /**
   * `setOverrideMasterTime` is called to override the `masterTime` that gets
   * passed down into the minimap.  This is used when dragging to seek; we
   * want the timeline to show the value we are seeking to, instead of the actual
   * play time.
   */
  setOverrideMasterTime(time: number | undefined): void;
  toggleShowAllEvents(): void;
  /** Called when the user clicks on "X more events..." in the timeline. */
  moreEventsClicked: () => void;
  updateDragState: (partialState: Partial<TimelineDragState>) => void;
}

export interface TimelineController {
  state: TimelineState;
  actions: TimelineActions;
}

/**
 * Returns a TimelineController object with information about the current masterTime,
 * the left-most and right-most times to show on the main chart and MiniMap,
 * and some functions for updating the time and seeking.
 *
 * This object updates very often, so if it's passed as a prop, expect the
 * react component that takes it to re-render often.
 *
 * @param params.playingLive - True if we are currently playing live.
 * @param params.timezone - The time zone to play in.
 * @param params.playerTime - The current play time.
 * @param params.hoveredEvent - The event the user is currently
 *   hovering over.  If this is "undefined", then TimelineController will
 *   keep track of the hovered event internally (like a React controlled vs
 *   uncontrolled component).
 * @param params.onHoveredEventChange - Funciton to call when the hovered event
 *   changes.
 * @param params.showAllEvents - True if all events should be shown in the
 *   timeline, false if only filtered events should be shown in the timeline.
 *   If undefined, then Timelinecontroller will keep track of this internally
 *   (like an uncontrolled component).
 * @param params.onShowAllEventsChange - Called when `showAllEvents` changes.
 * @param params.onMoreEventsClicked - Called when the user clicks on "X more events..." in the timeline.
 */
export function useTimelineController(params: {
  hoveredEvent?: TimelineEvent;
  onHoveredEventChange?: (event: TimelineEvent | undefined) => void;
  onMoreEventsClicked?: () => void;
  onShowAllEventsChange?: (showAllEvents: boolean) => void;
  playerTime: number;
  showAllEvents?: boolean;
  timezone: string;
  truncateEvents: boolean;
}): TimelineController {
  const { playerTime, timezone, onHoveredEventChange, onShowAllEventsChange, truncateEvents } =
    params;

  if (typeof playerTime !== 'number' || isNaN(playerTime)) {
    throw new Error(`Invalid player time ${playerTime} of type ${typeof playerTime}`);
  }

  const [uncontrolledHoveredEvent, setUncontrolledHoveredEvent] = useState<
    TimelineEvent | undefined
  >();
  const debouncedSetUncontrolledHoveredEvent = useDebouncedFunction(
    setUncontrolledHoveredEvent,
    HOVER_EVENT_DEBOUNCE_MS,
  );

  const onHoveredEventChangeRef = useLatest(onHoveredEventChange);

  const setHoveredEvent = useCallback(
    (event: TimelineEvent | undefined) => {
      debouncedSetUncontrolledHoveredEvent(event);
      onHoveredEventChangeRef.current?.(event);
    },
    [debouncedSetUncontrolledHoveredEvent, onHoveredEventChangeRef],
  );

  const hoveredEvent = params.hoveredEvent ?? uncontrolledHoveredEvent;

  const [uncontrolledShowAllEvents, setUncontrolledShowAllEvents] = useState(false);

  const [dragState, updateDragState] = useTimelineDrag();

  const showAllEvents = params.showAllEvents ?? uncontrolledShowAllEvents;
  const latestShowAllEvents = useLatest(showAllEvents);
  const latestOnShowAllEventsChange = useLatest(onShowAllEventsChange);

  const toggleShowAllEvents = useCallback(() => {
    const newValue = !latestShowAllEvents.current;
    setUncontrolledShowAllEvents(newValue);
    latestOnShowAllEventsChange.current?.(newValue);
  }, [latestOnShowAllEventsChange, latestShowAllEvents]);

  // Range of the main chart, in milliseconds.
  const [mainChartRange, rawSetMainChartRange] = useState(DEFAULT_MAIN_CHART_RANGE);
  const setMainChartRange = useMemo(
    () =>
      throttle((ms: number) => {
        const newRange = Math.max(MIN_MAIN_CHART_RANGE_MS, Math.min(MAX_MAIN_CHART_RANGE_MS, ms));
        rawSetMainChartRange(newRange);
      }, SET_RANGE_THROTTLE_MS),
    [],
  );

  // When seeking by dragging in the timeline, we temporarily override the
  // "masterTime" for components in the VideoPlayerFooter.  This makes
  // components seem responsive, even if the video player is sluggish to seek.
  const [overrideMasterTime, setOverrideMasterTime] = useState<number | undefined>();

  const latestOnMoreEventsClicked = useLatest(params.onMoreEventsClicked);
  const moreEventsClicked = useCallback(() => {
    latestOnMoreEventsClicked.current?.();
  }, [latestOnMoreEventsClicked]);

  // `actions` should never change.
  const actionsRef = useRef<TimelineActions>({
    setHoveredEvent,
    setMainChartRange,
    setOverrideMasterTime,
    toggleShowAllEvents,
    moreEventsClicked,
    updateDragState,
  });

  return useMemo(
    () => ({
      actions: actionsRef.current,
      state: getTimelineState(
        playerTime,
        timezone,
        hoveredEvent,
        overrideMasterTime,
        mainChartRange,
        showAllEvents,
        truncateEvents,
        dragState,
      ),
    }),
    [
      playerTime,
      timezone,
      hoveredEvent,
      overrideMasterTime,
      mainChartRange,
      showAllEvents,
      truncateEvents,
      dragState,
    ],
  );
}

function getTimelineState(
  playerTime: number,
  timezone: string,
  hoveredEvent: TimelineEvent | undefined,
  overrideMasterTime: number | undefined,
  mainChartRange: number,
  showAllEvents: boolean,
  truncateEvents: boolean,
  dragState: TimelineDragState,
): TimelineState {
  const playHeadTime = overrideMasterTime ?? playerTime;

  const mainChartStartTZ = moment(playHeadTime).tz(timezone).valueOf() - mainChartRange;
  const mainChartEndTZ = moment(playHeadTime).tz(timezone).valueOf() + mainChartRange;

  const miniMapStartTZ = moment(playHeadTime).tz(timezone).startOf('day').valueOf();
  const miniMapEndTZ = moment(playHeadTime).tz(timezone).endOf('day').valueOf();

  return {
    dragState,
    hoveredEvent,
    playHeadTime,
    timezone,
    mainChartRange,
    mainChartStart: mainChartStartTZ,
    mainChartEnd: mainChartEndTZ,
    // MiniMap always shows 24 hours based on the current play time.
    // TODO: We *could* move the scale around a bit in the minimap when we're near midnight?
    miniMapStart: miniMapStartTZ,
    miniMapEnd: miniMapEndTZ,
    fetchStart: miniMapStartTZ,
    fetchEnd: miniMapEndTZ,
    showAllEvents,
    eventLengthLimitMs: truncateEvents ? TRUNCATED_EVENT_LENGTH_LIMIT_MS : undefined,
  };
}
